import React, { useState, useEffect } from 'react';

import { Checkbox } from '@cision/rover-ui';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { RadioButton, SearchInput } from '@trendkite/ui';

import { Value } from '../widgetWizard/steps/new-widget-filters-tab/filters-definintions';
import {
  setSelectedFilterOneValueActionCreator,
  setSelectedFilterSeveralValuesActionCreator,
} from '../widgetWizard/steps/new-widget-filters-tab/new-widget-filters-reducer';
import { widgetFilterValuesSelector } from '../widgetWizard/steps/new-widget-filters-tab/new-widget-filters-selector';

import messages from './filter.messages';
import styles from './PersistentFilter.module.css';

const SEARCH_MAX_FILTERS = 15;

type KeyValue = { key: string; value: string };

const PersistentFilter = ({ widgetFilterId, isMultiValue }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const [selectAll, setSelectAll] = useState(false);
  const filterValues = useSelector(widgetFilterValuesSelector(widgetFilterId));
  const [searchQuery, setSearchQuery] = useState('');
  const [searchedFilterValues, setSearchedFilterValues] = useState([]);

  useEffect(() => {
    setSearchedFilterValues(filterValues);
  }, [filterValues]);

  useEffect(() => {
    if (filterValues && !searchQuery) {
      setSearchedFilterValues(filterValues);
    }
    if (filterValues?.length > SEARCH_MAX_FILTERS && searchQuery) {
      const filtered = filterValues.filter((value: KeyValue) =>
        value.key.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setSearchedFilterValues(filtered);
    }
  }, [searchQuery, filterValues]);

  const toggleValueCheckbox = e => {
    const selectedValue = filterValues.find(
      (v: Value) => v.value === e.currentTarget.id,
    );
    const toggledValue = {
      ...selectedValue,
      selected: !selectedValue.selected,
    };
    dispatch(
      setSelectedFilterOneValueActionCreator(widgetFilterId, toggledValue),
    );
    setSelectAll(false);
  };

  const toggleValueRadiobutton = e =>
    dispatch(
      setSelectedFilterSeveralValuesActionCreator(
        widgetFilterId,
        searchedFilterValues.map((v: Value) =>
          v.value === e.currentTarget.id
            ? { ...v, selected: !v.selected }
            : { ...v, selected: false },
        ),
      ),
    );

  const toggleSelectAll = () => {
    dispatch(
      setSelectedFilterSeveralValuesActionCreator(
        widgetFilterId,
        searchedFilterValues.map((v: Value) => {
          return { ...v, selected: !selectAll };
        }),
      ),
    );
    setSelectAll(!selectAll);
  };

  const getCorrespondingToggleOption = (e, isMultiValue) =>
    isMultiValue ? toggleValueCheckbox(e) : toggleValueRadiobutton(e);

  const renderFilterValues = () =>
    /*This type is type of any because the value which is the id of the filterValue
  will conflict with the Key and Id of the component. There is no current real ID for
  a filter value, it has to be addressed in a future ticket after data structure is modified*/
    searchedFilterValues?.map((v: any) => (
      <div
        data-qa="iw1CkbiQb8b9VbLIiTXOW"
        key={v.value}
        role="button"
        aria-hidden="true"
        id={v.value}
        className={styles.filterRow}
        onKeyDown={e => {
          getCorrespondingToggleOption(e, isMultiValue);
        }}
        onClick={e => {
          getCorrespondingToggleOption(e, isMultiValue);
        }}
      >
        {isMultiValue ? (
          <Checkbox checked={v.selected} />
        ) : (
          <RadioButton data-qa="_Ns8wmriEXdJ6lmJ-h28p" selected={v.selected} />
        )}
        <span className={styles.filterLabel}>{v.key}</span>
      </div>
    ));

  const renderSelectAllOption = () =>
    isMultiValue && (
      <div className={styles.filterRow}>
        <Checkbox checked={selectAll} onChange={toggleSelectAll} />
        <span className={styles.filterLabel}>Select All</span>
      </div>
    );

  const showSearchBar =
    filterValues && filterValues.length > SEARCH_MAX_FILTERS;

  return (
    <div
      className={styles.container}
      style={showSearchBar ? { paddingTop: 0 } : undefined}
    >
      {showSearchBar && (
        <div className={styles.searchBar}>
          <SearchInput
            placeholder={intl.formatMessage(
              messages.filterSearchBarPlaceholder,
            )}
            performSearchCallback={query => setSearchQuery(query)}
            modifiers={['slim']}
            searchQuery={searchQuery}
            style={{ borderBottomColor: 'transparent' }}
          />
        </div>
      )}
      {renderFilterValues()}
      {renderSelectAllOption()}
    </div>
  );
};

export default PersistentFilter;
